import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import {
  CalendarConfig,
  CalendarGenerationResult,
  CupConfig,
  IncomingTeamMove,
  PromotionImpactPreview,
  PromotionRule,
  SeasonRow,
  SeasonTransitionRow,
  SearchedTeam,
  TeamMoveRow,
  addIncomingTeamToGroup,
  carryForwardCupGroups,
  deletePromotionRule,
  listIncomingTeamMoves,
  previewPromotionImpact,
  previewCarryoverSuspensions,
  previewCarryoverConfigurations,
  getLeagueSeasonReadiness,
  setSuspensionCarryoverEscape,
  CarryoverSuspensionRow,
  CarryoverConfigsPreview,
  ReadinessRow,
  placeIncomingTeam,
  createSeasonRow,
  activateSeasonStandalone,
  createTeam,
  createTransition,
  generateCalendar,
  inviteTopNToCup,
  listCalendarConfigs,
  listPromotionRules,
  listSeasons,
  listTeamMoves,
  listTransitions,
  moveTeamBetweenGroups,
  resetTransition,
  runStep,
  searchTeams,
  fetchLeagueClubIds,
  simulateTransition,
  updateCupConfig,
  updateTeamMove,
  upsertCalendarConfig,
  upsertPromotionRule,
} from "../services/seasonTransitionService";

const KEYS = {
  seasons: ["season-transition", "seasons"] as const,
  rules: ["season-transition", "rules"] as const,
  transitions: ["season-transition", "transitions"] as const,
  moves: (id: string | null | undefined) =>
    ["season-transition", "moves", id ?? "none"] as const,
  simulation: (from: string, to: string) =>
    ["season-transition", "simulation", from, to] as const,
};

export function useSeasons() {
  return useQuery<SeasonRow[]>({ queryKey: KEYS.seasons, queryFn: listSeasons });
}

export function useCreateSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSeasonRow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.seasons });
      qc.invalidateQueries({ queryKey: ["appSettings"] });
    },
  });
}

export function useActivateSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: activateSeasonStandalone,
    onSuccess: (_data, code) => {
      qc.invalidateQueries({ queryKey: KEYS.seasons });
      qc.invalidateQueries({ queryKey: ["appSettings"] });
      qc.invalidateQueries({ queryKey: ["matches"] });
      useLeagueSeasonStore.getState().setCurrentSeason(code);
    },
  });
}

export function usePromotionRules() {
  return useQuery<PromotionRule[]>({ queryKey: KEYS.rules, queryFn: listPromotionRules });
}

export function useUpsertPromotionRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: upsertPromotionRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.rules }),
  });
}

export function useDeletePromotionRule() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deletePromotionRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.rules }),
  });
}

/**
 * Dry-run of the promotion/relegation rules against `fromSeason`'s current
 * standings — who goes up / down, and where each team lands.
 */
export function usePromotionImpactPreview(
  fromSeason: string | null | undefined,
  leagueId?: string | null
) {
  return useQuery<PromotionImpactPreview>({
    queryKey: [
      "season-transition",
      "promotion-impact",
      fromSeason ?? "none",
      leagueId ?? "all",
    ],
    queryFn: () => previewPromotionImpact(fromSeason as string, leagueId),
    enabled: !!fromSeason,
    staleTime: 30_000,
  });
}

/** Clone the from-season cup/play-off groups into the transition's to-season. */
export function useCarryForwardCupGroups() {
  const qc = useQueryClient();
  return useMutation<number, Error, string>({
    mutationFn: carryForwardCupGroups,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leagueGroups"] });
      qc.invalidateQueries({ queryKey: ["season-transition"] });
    },
  });
}

export function useTransitions() {
  return useQuery<SeasonTransitionRow[]>({
    queryKey: KEYS.transitions,
    queryFn: listTransitions,
  });
}

export function useCreateTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fromSeason, toSeason }: { fromSeason: string; toSeason: string }) =>
      createTransition(fromSeason, toSeason),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.transitions }),
  });
}

export function useRunStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      step,
      transitionId,
    }: {
      step: Parameters<typeof runStep>[0];
      transitionId: string;
    }) => runStep(step, transitionId),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.transitions });
      qc.invalidateQueries({ queryKey: KEYS.moves(vars.transitionId) });
      qc.invalidateQueries({
        queryKey: ["season-transition", "carryover-configs", vars.transitionId],
      });
    },
  });
}

export function useCarryoverSuspensionsPreview(
  transitionId: string | null | undefined,
  enabled = true
) {
  return useQuery<CarryoverSuspensionRow[]>({
    queryKey: ["season-transition", "carryover-suspensions", transitionId ?? "none"],
    queryFn: () => previewCarryoverSuspensions(transitionId as string),
    enabled: !!transitionId && enabled,
  });
}

export function useCarryoverConfigsPreview(
  transitionId: string | null | undefined,
  enabled = true
) {
  return useQuery<CarryoverConfigsPreview>({
    queryKey: ["season-transition", "carryover-configs", transitionId ?? "none"],
    queryFn: () => previewCarryoverConfigurations(transitionId as string),
    enabled: !!transitionId && enabled,
  });
}

export function useLeagueSeasonReadiness(
  leagueId: string | null,
  season: string | null | undefined,
  enabled = true
) {
  return useQuery<ReadinessRow[]>({
    queryKey: ["league-season-readiness", leagueId ?? "all", season ?? "none"],
    queryFn: () => getLeagueSeasonReadiness(season as string, leagueId),
    enabled: !!season && enabled,
    staleTime: 60_000,
  });
}

export function useSetSuspensionCarryoverEscape() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { transitionId: string; sanctionId: string; escaped: boolean }
  >({
    mutationFn: ({ transitionId, sanctionId, escaped }) =>
      setSuspensionCarryoverEscape(transitionId, sanctionId, escaped),
    onSuccess: (_, v) => {
      qc.invalidateQueries({
        queryKey: ["season-transition", "carryover-suspensions", v.transitionId],
      });
      qc.invalidateQueries({ queryKey: KEYS.transitions });
    },
  });
}

export function useResetTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (transitionId: string) => resetTransition(transitionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.transitions });
      qc.invalidateQueries({ queryKey: ["season-transition"] });
    },
  });
}

export function useTeamMoves(transitionId: string | null | undefined) {
  return useQuery<TeamMoveRow[]>({
    queryKey: KEYS.moves(transitionId),
    queryFn: () => listTeamMoves(transitionId as string),
    enabled: !!transitionId,
  });
}

export function useUpdateTeamMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transitionId,
      teamId,
      patch,
    }: {
      transitionId: string;
      teamId: string;
      patch: Parameters<typeof updateTeamMove>[2];
    }) => updateTeamMove(transitionId, teamId, patch),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: KEYS.moves(vars.transitionId) }),
  });
}

export function useSimulation(fromSeason: string | null, toSeason: string | null) {
  return useQuery<Record<string, unknown>>({
    queryKey: KEYS.simulation(fromSeason ?? "", toSeason ?? ""),
    queryFn: () => simulateTransition(fromSeason as string, toSeason as string),
    enabled: !!fromSeason && !!toSeason,
    staleTime: 30_000,
  });
}

// ── Calendar config + generator ─────────────────────────────────────────────

const CALENDAR_KEYS = {
  configs: ["season-transition", "calendar-configs"] as const,
};

export function useCalendarConfigs() {
  return useQuery<CalendarConfig[]>({
    queryKey: CALENDAR_KEYS.configs,
    queryFn: listCalendarConfigs,
  });
}

export function useUpsertCalendarConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: upsertCalendarConfig,
    onSuccess: () => qc.invalidateQueries({ queryKey: CALENDAR_KEYS.configs }),
  });
}

export function useGenerateCalendar() {
  const qc = useQueryClient();
  return useMutation<
    CalendarGenerationResult,
    Error,
    {
      groupId: string;
      dryRun: boolean;
      override?: import("../services/seasonTransitionService").CalendarOverride;
    }
  >({
    mutationFn: ({ groupId, dryRun, override }) =>
      generateCalendar(groupId, dryRun, override),
    onSuccess: (_, vars) => {
      if (!vars.dryRun) {
        // Matches were committed — invalidate any match queries (they may live under various keys)
        qc.invalidateQueries({ queryKey: ["matches"] });
      }
    },
  });
}

export function useUpdateCupConfig() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { groupId: string; patch: Partial<CupConfig> }
  >({
    mutationFn: ({ groupId, patch }) => updateCupConfig(groupId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leagueGroups"] });
    },
  });
}

export function useInviteTopNToCup() {
  const qc = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof inviteTopNToCup>>,
    Error,
    { cupGroupId: string; replace?: boolean }
  >({
    mutationFn: ({ cupGroupId, replace }) => inviteTopNToCup(cupGroupId, replace),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leagueGroups"] });
      qc.invalidateQueries({ queryKey: ["league-group-teams"] });
    },
  });
}

/**
 * Club search for the incoming-team picker. Scoped to the clubs of `leagueId`
 * (any season, club rows only); `includeOtherLeagues` widens it to every club
 * row — an explicit opt-in for promotions from another league or a wilaya.
 */
export function useSearchTeams(
  query: string,
  excludeIds: string[] = [],
  opts: { leagueId?: string | null; includeOtherLeagues?: boolean } = {}
) {
  const { leagueId = null, includeOtherLeagues = false } = opts;
  const qc = useQueryClient();
  const scoped = !includeOtherLeagues;
  return useQuery<SearchedTeam[]>({
    queryKey: ["team-search", query, excludeIds.join(","), scoped ? leagueId : "all"],
    queryFn: async () => {
      const clubIds = !scoped
        ? null
        : leagueId
          ? await qc.ensureQueryData({
              queryKey: ["league-club-ids", leagueId],
              queryFn: () => fetchLeagueClubIds(leagueId),
              staleTime: 5 * 60_000,
            })
          : [];
      return searchTeams(query, excludeIds, { clubIds });
    },
    enabled: query.length > 0 || excludeIds.length > 0,
    staleTime: 30_000,
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation<
    SearchedTeam,
    Error,
    { name: string; code: string; name_ar?: string | null }
  >({
    mutationFn: createTeam,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

export function useAddIncomingTeam() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    {
      groupId: string;
      teamId: string;
      transitionId?: string | null;
      origin?: string | null;
      toLeagueId?: string | null;
    }
  >({
    mutationFn: addIncomingTeamToGroup,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["leagueGroups"] });
      qc.invalidateQueries({ queryKey: ["league-group-teams"] });
      if (vars.transitionId) {
        qc.invalidateQueries({
          queryKey: ["season-transition", "moves", vars.transitionId],
        });
      }
    },
  });
}

/**
 * Cross-league teams (promotion/relegation/admission) targeting this league for
 * `toSeason` that still await a group placement — the destination league's
 * onboarding inbox.
 */
export function useIncomingTeamMoves(
  leagueId: string | null | undefined,
  toSeason: string | null | undefined
) {
  return useQuery<IncomingTeamMove[]>({
    queryKey: ["season-transition", "incoming", leagueId ?? "none", toSeason ?? "none"],
    queryFn: () => listIncomingTeamMoves(leagueId as string, toSeason as string),
    enabled: !!leagueId && !!toSeason,
  });
}

export function usePlaceIncomingTeam() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { transitionId: string; teamId: string; groupId: string }
  >({
    mutationFn: placeIncomingTeam,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["season-transition", "incoming"] });
      qc.invalidateQueries({ queryKey: ["leagueGroups"] });
      qc.invalidateQueries({ queryKey: ["league-group-teams"] });
      qc.invalidateQueries({ queryKey: ["groupCalendarStatus"] });
    },
  });
}

export function useMoveTeamBetweenGroups() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { teamId: string; fromGroupId: string | null; toGroupId: string | null }
  >({
    mutationFn: ({ teamId, fromGroupId, toGroupId }) =>
      moveTeamBetweenGroups(teamId, fromGroupId, toGroupId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["league-group-teams"] });
      qc.invalidateQueries({ queryKey: ["leagueGroups"] });
    },
  });
}
